/**
 * useCheckInDeviceIdentities — resolve per-device make/serial for a check-in
 * work item.
 *
 * The work item only carries the draft-job *summary* (`checkInDraftJobs` →
 * type, service summary, inspection status). The manufacturer + serial live on
 * the full session's draft-job inspections, so we fetch the session (via
 * useCheckInSession, which is SignalR-aware) and expose a
 * `{ draftJobId → identity }` lookup.
 *
 * This covers both check-in work items and jobs converted from a check-in: the
 * backend attaches `checkInSessionGuid` to both, so the presence of that guid —
 * not the work-item type — decides whether we fetch. Items with no linked
 * session pass a null id, so no fetch happens.
 */
import { useMemo } from 'react';
import type {
  CheckInDeviceType,
  CheckInDraftJob,
  CheckInInspection,
  CheckInIntakeType,
} from '@techgitter/contracts';
import type { WorkItem } from '../components/workitems/workItemTypes';
import { useCheckInSession } from './useCheckInSession';

export interface CheckInDeviceIdentity {
  manufacturer: string | null;
  serialNumber: string | null;
  /** The manufacturer's model number (A-number, MTM, HP P/N). */
  modelNumber: string | null;
  /** Apple computers only — tells apart Macs sharing an A-number. */
  emcNumber: string | null;
  serialNotLegible: boolean;
  brandNewInBox: boolean;
}

/** Pull the make/serial identity off a device inspection (null before the
 *  device has been inspected). */
export function inspectionIdentity(
  inspection: CheckInInspection | null | undefined,
): CheckInDeviceIdentity | null {
  if (!inspection) return null;
  return {
    manufacturer: inspection.manufacturer ?? null,
    serialNumber: inspection.serialNumber ?? null,
    modelNumber: inspection.modelNumber ?? null,
    emcNumber: inspection.emcNumber ?? null,
    // `serialNotLegible` only exists on laptop/phone/tablet inspections.
    serialNotLegible:
      'serialNotLegible' in inspection ? Boolean(inspection.serialNotLegible) : false,
    brandNewInBox: Boolean(inspection.brandNewInBox),
  };
}

/** Compact display parts for a device identity, e.g.
 *  ["Apple", "Model A2338", "EMC 3598", "S/N ABC123"]. The model number is what
 *  parts are looked up by, so it sits beside the make. Serial is labelled IMEI
 *  for phones. Returns [] when nothing is known. */
export function formatDeviceIdentityParts(
  identity: CheckInDeviceIdentity | null | undefined,
  deviceType?: CheckInDeviceType,
): string[] {
  if (!identity) return [];
  const parts: string[] = [];
  if (identity.manufacturer) parts.push(identity.manufacturer);
  if (identity.modelNumber) parts.push(`Model ${identity.modelNumber}`);
  if (identity.emcNumber) parts.push(`EMC ${identity.emcNumber}`);
  const serialLabel = deviceType === 'phone' ? 'IMEI' : 'S/N';
  if (identity.serialNumber) parts.push(`${serialLabel} ${identity.serialNumber}`);
  else if (identity.serialNotLegible) parts.push(`${serialLabel} not legible`);
  if (identity.brandNewInBox) parts.push('New in box');
  return parts;
}

export interface UseCheckInDeviceIdentitiesResult {
  /** Keyed by draft-job id. Empty until the session loads. */
  identities: Record<string, CheckInDeviceIdentity | null>;
  /**
   * The full draft jobs behind those identities, keyed the same way. The
   * work-item summary carries only device type + inspection status, but a
   * serial correction has to PATCH the whole inspection blob — and we've
   * already paid for the session fetch, so it's handed back rather than
   * dropped.
   */
  draftJobs: Record<string, CheckInDraftJob>;
  /** Intake type of the linked check-in session (null until it loads / none). */
  intakeType: CheckInIntakeType | null;
  isLoading: boolean;
  /** Refetch the session — call after editing a device. */
  refresh: () => void;
}

export function useCheckInDeviceIdentities(
  workItem: WorkItem,
): UseCheckInDeviceIdentitiesResult {
  // Fetch whenever the work item is linked to a check-in session — check-in
  // rows and jobs converted from a check-in both carry checkInSessionGuid.
  const sessionId = workItem.checkInSessionGuid ?? null;
  const { session, isLoading, refresh } = useCheckInSession(sessionId);

  const identities = useMemo(() => {
    const map: Record<string, CheckInDeviceIdentity | null> = {};
    for (const dj of session?.draftJobs ?? []) {
      map[dj.id] = inspectionIdentity(dj.inspection);
    }
    return map;
  }, [session]);

  const draftJobs = useMemo(() => {
    const map: Record<string, CheckInDraftJob> = {};
    for (const dj of session?.draftJobs ?? []) {
      map[dj.id] = dj;
    }
    return map;
  }, [session]);

  return {
    identities,
    draftJobs,
    intakeType: session?.intakeType ?? null,
    isLoading,
    refresh,
  };
}
